/**
 * ContactStrip — контактные pill-ссылки в финале страницы.
 */
import { MessageCircle, Send, Mail, ArrowUpRight } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { useLang } from "../i18n";
import { useReveal } from "../lib/motion";
import { CONTACTS, isPlaceholder } from "../content/contacts";

// Lucide-react v1.14.0 не экспортирует Instagram icon (brand-policy).
// Используем inline SVG с тем же visual API что у Lucide компонентов.
const Instagram: ComponentType<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

type ContactItem = {
  key: "whatsapp" | "telegram" | "instagram" | "email";
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  shown: boolean;
};

export function ContactStrip() {
  const { t } = useLang();
  const ref = useReveal<HTMLElement>();

  const encodedMessage = encodeURIComponent(t.contact.message);

  const items: ContactItem[] = [
    {
      key: "whatsapp",
      href: `https://wa.me/${CONTACTS.whatsapp}?text=${encodedMessage}`,
      icon: MessageCircle,
      shown: !isPlaceholder(CONTACTS.whatsapp),
    },
    {
      key: "telegram",
      href: `https://t.me/${CONTACTS.telegram}`,
      icon: Send,
      shown: !isPlaceholder(CONTACTS.telegram),
    },
    {
      key: "instagram",
      href: `https://instagram.com/${CONTACTS.instagram}`,
      icon: Instagram,
      shown: !isPlaceholder(CONTACTS.instagram),
    },
    {
      key: "email",
      href: `mailto:${CONTACTS.email}?subject=${encodeURIComponent("Orda OS — заявка")}&body=${encodedMessage}`,
      icon: Mail,
      shown: !isPlaceholder(CONTACTS.email),
    },
  ];

  const visible = items.filter((i) => i.shown);

  return (
    <section
      id="contact"
      ref={ref}
      className="border-t"
      style={{ borderColor: "var(--line)", background: "var(--bg-deep)" }}
    >
      <div className="mx-auto w-full max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <p className="t-eyebrow reveal mb-3" data-reveal="">
              {t.contact.title}
            </p>
            <p
              className="t-serif-i reveal text-2xl sm:text-3xl"
              data-reveal=""
              style={{ color: "var(--fg)" }}
            >
              {t.contact.subtitle}
            </p>
          </div>

          <div className="reveal flex flex-wrap gap-3" data-reveal="">
            {visible.map(({ key, href, icon: Icon }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener"
                className="group inline-flex items-center gap-2.5 rounded-full border px-5 py-3 text-sm font-bold transition-all hover:-translate-y-0.5"
                style={{ borderColor: "var(--line-strong)", color: "var(--fg)" }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--gold-line)")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--line-strong)")}
              >
                <Icon className="h-4 w-4" style={{ color: "var(--gold)" }} aria-hidden />
                {t.contact[key]}
                <ArrowUpRight
                  className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100"
                  aria-hidden
                />
              </a>
            ))}
          </div>
        </div>

        {visible.length === 0 ? (
          <p
            className="mt-6 rounded-2xl p-4 text-sm"
            style={{
              background: "rgba(212,166,86,0.08)",
              color: "var(--gold-bright)",
              border: "1px solid var(--gold-line)",
            }}
          >
            ⚠️ Контакты не настроены — заполни TODO_* в src/content/contacts.ts перед launch'ем.
          </p>
        ) : null}
      </div>
    </section>
  );
}
